import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import './CourseRates.css';

const API = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API}/api/admin/Prices/package-bonus`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const TIERS = [8, 24, 40];
const TYPES = [
  ['standard', 'Standard'],
  ['mastery', 'Mastery & Robotics'],
];

/* Prices & Offers › Teacher package bonus.
   Every paid package of 8+ sessions adds a bonus to a teacher's earnings, automatically:
   a new student -> the teacher who did the demo; a renewal -> the student's teacher. */
const PackageBonus = () => {
  const [cfg, setCfg] = useState(null); // saved settings
  const [form, setForm] = useState(null); // what is being edited
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(BASE, auth())
      .then((res) => {
        if (!alive) return;
        setCfg(res.data);
        setForm(toForm(res.data));
      })
      .catch((err) => alive && setMsg({ ok: false, text: err.response?.data?.error || 'We couldn’t load the bonus settings.' }));
    return () => {
      alive = false;
    };
  }, []);

  const toForm = (c) => ({
    enabled: !!c.enabled,
    renewals: !!c.renewals,
    startDate: c.startDate || '',
    rates: Object.fromEntries(TYPES.map(([t]) => [t, Object.fromEntries(TIERS.map((n) => [n, String(c.rates?.[t]?.[n] ?? '')]))])),
  });

  const changed = cfg && form && JSON.stringify(toForm(cfg)) !== JSON.stringify(form);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const rates = Object.fromEntries(TYPES.map(([t]) => [t, Object.fromEntries(TIERS.map((n) => [n, Number(form.rates[t][n] || 0)]))]));
      const res = await axios.put(BASE, { enabled: form.enabled, renewals: form.renewals, startDate: form.startDate || null, rates }, auth());
      setCfg(res.data);
      setForm(toForm(res.data));
      setMsg({ ok: true, text: 'Bonus settings saved. Teachers see them on their Earnings page.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Couldn’t save.' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <section className="rmcr" aria-labelledby="rmpb-title">
        <h2 id="rmpb-title">
          <FontAwesomeIcon icon={faGift} /> Teacher package bonus
        </h2>
        {msg ? <p className="rmcr-msg is-bad">{msg.text}</p> : <div className="rmcr-skel" />}
      </section>
    );
  }

  return (
    <section className="rmcr" aria-labelledby="rmpb-title">
      <h2 id="rmpb-title">
        <FontAwesomeIcon icon={faGift} /> Teacher package bonus
      </h2>
      <p className="rmcr-sub">
        Added automatically to the teacher’s earnings when you add a package in the student’s Payments tab. A <b>new student</b> gives the
        bonus to the teacher who did the demo{form.renewals ? <>; a <b>renewal</b> gives it to the student’s teacher</> : ' (renewals give no bonus)'}.
        Packages under 8 sessions get no bonus; other sizes count as the size below them (20 sessions = the 8 bonus). You can remove a bonus
        (refund, wrong case) on the teacher’s Earnings tab.
      </p>
      {msg && (
        <p className={`rmcr-msg ${msg.ok ? '' : 'is-bad'}`} role="status">
          <FontAwesomeIcon icon={msg.ok ? faCircleCheck : faTriangleExclamation} /> {msg.text}
        </p>
      )}

      <div className="rmpb-top">
        <label className="rmpb-switch">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} disabled={saving} />
          <span>{form.enabled ? 'Bonuses are on' : 'Bonuses are off'}</span>
        </label>
        <label className="rmpb-switch">
          <input type="checkbox" checked={form.renewals} onChange={(e) => setForm((f) => ({ ...f, renewals: e.target.checked }))} disabled={saving || !form.enabled} />
          <span>Also on renewals</span>
        </label>
        <label className="rmpb-date">
          <span>Payments from</span>
          <input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} disabled={saving} />
        </label>
      </div>

      <div className="rmpb-table" role="table" aria-label="Bonus per package">
        <div className="rmpb-row rmpb-row--head" role="row">
          <span role="columnheader">Package</span>
          {TIERS.map((n) => (
            <span key={n} role="columnheader">
              {n} sessions
            </span>
          ))}
        </div>
        {TYPES.map(([t, label]) => (
          <div key={t} className="rmpb-row" role="row">
            <b role="rowheader">{label}</b>
            {TIERS.map((n) => (
              <label key={n} className="rmpb-cell" role="cell">
                <span>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  inputMode="decimal"
                  value={form.rates[t][n]}
                  onChange={(e) => setForm((f) => ({ ...f, rates: { ...f.rates, [t]: { ...f.rates[t], [n]: e.target.value } } }))}
                  disabled={saving}
                  aria-label={`Bonus for ${n} ${label} sessions`}
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      <div className="rmpb-actions">
        <button type="button" className="rmpb-save" onClick={save} disabled={!changed || saving}>
          {saving ? 'Saving…' : 'Save bonus settings'}
        </button>
      </div>
    </section>
  );
};

export default PackageBonus;
